import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import useRequestForQuotesForm from "@WhiteCap/Hooks/useRequestForQuotesForm";
import getColor from "@insite/mobius/utilities/getColor";
import { isGuestUserBasicInfoCompleted } from "@WhiteCap/Widgets/GuestRFQ/CustomerBasicInfo";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { connect, ResolveThunks } from "react-redux";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import siteMessage from "@insite/client-framework/SiteMessage";

{
    /* EC-235 : SubTask - EC-528 = Wizard Structure of the form (Level 2) i.e Customer Billing and Shipping Info */
}

// Mapping state to props for Redux state access
const mapStateToProps = (state: CustomApplicationState) => {
    const session = getSession(state);
    const currentUrl = getLocation(state);
    return {
        isAuthenticated: session.isAuthenticated,
        countries: getCurrentCountries(state),
        currentUrl,
    };
};

const mapDispatchToProps = {
    loadCurrentCountries,
};

// Define the types for component props
type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

// Interface for defining the styles for the component
export interface QouteCartFormStyles {
    formHeading?: InjectableCss;
    formContainer?: InjectableCss;
    formInner?: InjectableCss;
    btnWrapper?: InjectableCss;
    selectProvince?: SelectPresentationProps;
    countrySelction?: InjectableCss;
    provinceWrapper?: InjectableCss;
    checkboxStyle?: CheckboxProps;
    shippingInputStyle?: InjectableCss;
    previousBtnStyles?: ButtonPresentationProps;
    proceedBtnStyles?: ButtonPresentationProps;
    toolTipStyles?: TooltipPresentationProps;
}

// Define default styles for the form and its components
export const qouteCartFormStyles: QouteCartFormStyles = {
    formContainer: {
        css: css`
            border: 1px solid ${getColor("secondary")};
            margin-top: 30px;
        `,
    },
    formHeading: {
        css: css`
            padding: 15px 0px 15px 20px;
            background: ${getColor("secondary")};
            border-bottom: 1px solid ${getColor("secondary")};
            display: flex;
            justify-content: flex-start;
            align-items: center;
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            align-items: flex-start;
                        `,
                        css`
                            align-items: flex-start;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
            h3 {
                color: ${getColor("text.main")};
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                font-size: 18px;
                line-height: 26px;
                margin-right: 20px;
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 16px;
                            `,
                            css`
                                font-size: 16px;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    formInner: {
        css: css`
            padding: 20px 30px 20px;
            label {
                font-weight: 500;
            }
        `,
    },
    btnWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 30px;
        `,
    },
    selectProvince: {
        css: css`
            background: ${getColor("primary.contrast")} !important;
            border: 1px solid ${getColor("secondary")} !important;
            border-radius: 1px !important;
            font-size: 15px !important;
            line-height: 1.42857143 !important;
            font-weight: 400 !important;
            border-width: 1px !important;
            height: 38px !important;
            &:focus {
                outline: none !important;
            }
            &:disabled {
                border: 1px solid ${getColor("secondary")} !important;
                background: unset !important;
                background-color: ${WhitecapColors.gray1} !important;
                opacity: unset;
                color: ${getColor("secondary")} !important;
            }
            &:disabled + span {
                background: none !important;
                svg {
                    background: none !important;
                }
            }
            &.error-border {
                border: 2px solid ${getColor("danger.main")} !important;
            }
        `,
    },
    provinceWrapper: {
        css: css`
            span[class^="IconWrapper"] {
                background-color: ${getColor("primary.contrast")} !important;
                height: 34px !important;
                svg {
                    color: ${getColor("text.main")};
                    background-color: ${getColor("primary.contrast")} !important;
                }
            }

            div[class^="FormFieldStyle"] {
                &:disabled {
                    span[class^="IconWrapper"] {
                        background-color: ${getColor("secondary")} !important;
                        svg {
                            background-color: ${getColor("secondary")} !important;
                        }
                    }
                }
            }
        `,
    },
    countrySelction: {
        css: css`
            border: 1px solid ${getColor("secondary")} !important;
            background-color: ${WhitecapColors.gray1} !important;
            color: ${getColor("secondary")} !important;
            text-transform: uppercase;
        `,
    },
    checkboxStyle: {
        css: css`
            cursor: pointer;
            span {
                background-color: ${getColor("primary.contrast")};
            }
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 10px;
                            label {
                                font-size: 12px;
                            }
                        `,
                        css`
                            margin-top: 10px;
                            label {
                                font-size: 12px;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    shippingInputStyle: {
        css: css`
            &:disabled {
                border: 1px solid ${getColor("secondary")} !important;
                background-color: ${WhitecapColors.gray1} !important;
                color: ${WhitecapColors.gray3} !important;
            }
        `,
    },
    toolTipStyles:{
        cssOverrides: {
            tooltipWrapper: css`
                margin-left: 5px;
            `,
            tooltipContainer: css`
                width: 200px;
                margin-left: 0px;
            &::after {
                    left: 150px;
            }
                `,
        },
        typographyProps: {
            weight: "normal",
        },
    }
};

// Styles object to be used throughout the component
const styles = qouteCartFormStyles;
const StyledForm = getStyledWrapper("form");

// Helper function to check if guest user address form is complete
// This will be used in Preview and Edit Section (CustomerFormPreview.tsx)
export const isGuestUserAddressComplete = (): boolean => {
    const formData = JSON.parse(sessionStorage.getItem("guestRfqFormData") || "{}");
    const requiredFields = [
        "billingAddress",
        "billingCity",
        "billingProvince",
        "billingPostalCode",
        "shippingAddress",
        "shippingCity",
        "shippingProvince",
        "shippingPostalCode",
    ];
    return requiredFields.every(field => !!formData[field]);
};

// Helper function to check if all guest user forms are completed
export const isGuestUserFormsCompleted = (): boolean => {
    const basicInfoCompleted = isGuestUserBasicInfoCompleted();
    const billingInfoCompleted = isGuestUserAddressComplete();
    return basicInfoCompleted && billingInfoCompleted;
};

const GuestUserAddressInfoForm = ({ history, isAuthenticated, countries, loadCurrentCountries, currentUrl }: Props) => {
    const guestUserAddedProduct = localStorage.getItem("rfqCart");
	
     useEffect(() => {
        // Show an alert message when the user tries to close the browser
        const alertUser = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", alertUser);
        return () => window.removeEventListener("beforeunload", alertUser);
    }, []);


    useEffect(() => {
        if (!countries?.length) loadCurrentCountries();
    }, [loadCurrentCountries]);

    const canada = countries?.find(country => country.name === "Canada" || country.abbreviation === "CA");
    const states = canada ? canada.states || [] : [];

    // Effect to handle redirection based on user authentication and form status
    useEffect(() => {
        const cart = guestUserAddedProduct ? JSON.parse(guestUserAddedProduct) : null;
        const isCartEmpty = !cart || cart.quoteCount <= 0;

        if (currentUrl.pathname.includes("/Content/Page")) {
            // Don't do anything, just display the page for content admin
            return;
        }

        if (!isGuestUserBasicInfoCompleted() && !isAuthenticated) {
            history.push("/GuestUserForm");
        } else if (isAuthenticated) {
            history.push("/");
        }

        if (isAuthenticated && !isCartEmpty) {
            // If user is authenticated and cart is not empty and clear local storage
            localStorage.removeItem("rfqCart");
            sessionStorage.removeItem("guestRfqFormData");
            history.push("/");
        }
    }, [history, guestUserAddedProduct, isAuthenticated]);

    // Hook to manage form state and validations
    const { formData, errors, handleChange, validateForm, setFormDataInLocalStorage, inputRefs } =
        useRequestForQuotesForm();
    const [sameAsBilling, setSameAsBilling] = useState(false);

    // Sync checkbox state with localStorage
    useEffect(() => {
        const storedValue = localStorage.getItem("sameAsBilling");
        if (storedValue !== null) {
            setSameAsBilling(JSON.parse(storedValue));
        }
    }, []);

    useEffect(() => {
        if (sameAsBilling) {
            handleChange("shippingRecipientName", formData.billingName);
            handleChange("shippingAddress", formData.billingAddress);
            handleChange("shippingCity", formData.billingCity);
            handleChange("shippingProvince", formData.billingProvince);
            handleChange("shippingPostalCode", formData.billingPostalCode);
        }
    }, [
        formData.billingName,
        formData.billingAddress,
        formData.billingCity,
        formData.billingProvince,
        formData.billingPostalCode,
        sameAsBilling,
    ]);

    useEffect(() => {
        localStorage.setItem("sameAsBilling", JSON.stringify(sameAsBilling));
    }, [sameAsBilling]);

    const handleCheckboxChange = () => {
        const updatedSameAsBilling = !sameAsBilling; // Toggle the state
        setSameAsBilling(updatedSameAsBilling);

        if (updatedSameAsBilling) {
            // Copy billing info to shipping fields if "Same as Billing" is selected
            handleChange("isShippingSameAsBilling", true);
            handleChange("shippingRecipientName", formData.billingName);
            handleChange("shippingAddress", formData.billingAddress);
            handleChange("shippingCity", formData.billingCity);
            handleChange("shippingProvince", formData.billingProvince);
            handleChange("shippingPostalCode", formData.billingPostalCode);
        } else {
            // Clear shipping fields if "Same as Billing" is deselected
            handleChange("isShippingSameAsBilling", false);
            handleChange("shippingRecipientName", "");
            handleChange("shippingAddress", "");
            handleChange("shippingCity", "");
            handleChange("shippingProvince", "");
            handleChange("shippingPostalCode", "");
        }
    };

    // Save form data and navigate to the next step
    const saveAndContinue = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        const isValid = validateForm([
            "billingAddress",
            "billingCity",
            "billingProvince",
            "billingPostalCode",
            "shippingAddress",
            "shippingCity",
            "shippingProvince",
            "shippingPostalCode",
        ]);
        if (isValid) {
            setFormDataInLocalStorage();
            history.push("/quote-preview-and-edit");
        }
    };

    // Redirect user to the previous form
    const redirectToPreviousState = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        history.push("/guest-user-form");
    };

    const handlePostalCodeKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === " ") {
            event.preventDefault();
        }
    };

    const handlePostalCodeInput = (event: React.FormEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        input.value = input.value.replace(/\s/g, "").toUpperCase();
    };


    return (
        <StyledWrapper>
            <StyledForm noValidate>
                {/* Billing Information Section */}
                <StyledWrapper {...styles.formContainer}>
                    <StyledWrapper {...styles.formHeading}>
                        <Typography as="h3">{translate("Billing Information")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.formInner}>
                        <GridContainer>
                            <GridItem width={[12, 12, 4, 4, 4]}>
                                <TextField
                                    label={translate("Name")}
                                    placeholder={translate("Billing Name")}
                                    data-test-selector="billing_name"
                                    value={formData.billingName}
                                    onChange={e => handleChange("billingName", e.currentTarget.value)}
                                    maxLength={200}
                                />
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]}>
                                <TextField
                                    label={translate("Address")}
                                    data-test-selector="address"
                                    placeholder={translate("Address")}
                                    value={formData.billingAddress}
                                    error={errors.billingAddress}
                                    ref={el => (inputRefs.current.billingAddress = el as HTMLInputElement)}
                                    onChange={e => handleChange("billingAddress", e.currentTarget.value)}
                                    maxLength={256}
                                    required
                                />
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]}>
                                <TextField
                                    label={translate("City")}
                                    placeholder={translate("City")}
                                    data-test-selector="city"
                                    value={formData.billingCity}
                                    error={errors.billingCity}
                                    ref={el => (inputRefs.current.billingCity = el as HTMLInputElement)}
                                    onChange={e => handleChange("billingCity", e.currentTarget.value)}
                                    maxLength={100}
                                    required
                                />
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]} {...styles.provinceWrapper}>
                                <Select
                                    label={translate("Province")}
                                    {...styles.selectProvince}
                                    value={formData.billingProvince}
                                    error={errors.billingProvince}
                                    onChange={e => handleChange("billingProvince", e.currentTarget.value)}
                                    ref={el => (inputRefs.current.billingProvince = el as HTMLInputElement)}
                                    data-test-selector="province"
                                    className={errors.billingProvince ? "error-border" : ""}
                                    required
                                >
                                    <option value="">{translate("Choose a Province")}</option>
                                    {states.map(state => (
                                        <option value={state.id} key={state.abbreviation} id={state.id}>
                                            {state.name}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]}>
                                <TextField
                                    label={
                                        <>
                                            {translate("Postal Code")}
                                            <Tooltip
                                                {...styles.toolTipStyles}
                                                text={siteMessage("postal_code_tooltip_message") as string}
                                            />
                                        </>
                                    }
                                    placeholder={translate("Postal Code")}
                                    data-test-selector="postal_code"
                                    value={formData.billingPostalCode}
                                    error={errors.billingPostalCode}
                                    onKeyDown={handlePostalCodeKeyDown}
                                    onInput={handlePostalCodeInput}  
                                    ref={el => (inputRefs.current.billingPostalCode = el as HTMLInputElement)}
                                    onChange={e => handleChange("billingPostalCode", e.currentTarget.value)}
                                    maxLength={6}
                                    required
                                />
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]}>
                                <TextField
                                    {...styles.countrySelction}
                                    label={translate("Country")}
                                    placeholder={canada ? canada.name : translate("Select Country")}
                                    value={canada ? canada.name : ""}
                                    data-test-selector="country"
                                    readOnly={true}
                                    disabled
                                    maxLength={20}
                                    required
                                />
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </StyledWrapper>

                {/* Shipping Information Section */}
                <StyledWrapper {...styles.formContainer}>
                    <StyledWrapper {...styles.formHeading}>
                        <Typography as="h3">{translate("Shipping Information")}</Typography>
                        <Checkbox
                            {...styles.checkboxStyle}
                            onChange={handleCheckboxChange}
                            checked={sameAsBilling}
                            data-test-selector="same-as-billing-checkbox"
                        >
                            {translate("Same as Billing Information")}
                        </Checkbox>
                    </StyledWrapper>
                    <StyledWrapper {...styles.formInner}>
                        <GridContainer>
                            <GridItem width={[12, 12, 4, 4, 4]}>
                                <TextField
                                    label={translate("Recipient Name")}
                                    placeholder={translate("Recipient Name")}
                                    data-test-selector="recipient_name"
                                    value={formData.shippingRecipientName}
                                    error={errors.shippingRecipientName}
                                    disabled={sameAsBilling}
                                    {...styles.shippingInputStyle}
                                    onChange={e => handleChange("shippingRecipientName", e.currentTarget.value)}
                                    maxLength={200}
                                />
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]}>
                                <TextField
                                    label={translate("Address")}
                                    data-test-selector="address"
                                    placeholder={translate("Address")}
                                    value={formData.shippingAddress}
                                    error={errors.shippingAddress}
                                    disabled={sameAsBilling}
                                    {...styles.shippingInputStyle}
                                    onChange={e => handleChange("shippingAddress", e.currentTarget.value)}
                                    ref={el => (inputRefs.current.shippingAddress = el as HTMLInputElement)}
                                    maxLength={256}
                                    required
                                />
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]}>
                                <TextField
                                    label={translate("City")}
                                    placeholder={translate("City")}
                                    data-test-selector="city"
                                    value={formData.shippingCity}
                                    error={errors.shippingCity}
                                    disabled={sameAsBilling}
                                    {...styles.shippingInputStyle}
                                    onChange={e => handleChange("shippingCity", e.currentTarget.value)}
                                    ref={el => (inputRefs.current.shippingCity = el as HTMLInputElement)}
                                    maxLength={100}
                                    required
                                />
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]} {...styles.provinceWrapper}>
                                <Select
                                    label={translate("Province")}
                                    {...styles.selectProvince}
                                    data-test-selector="province"
                                    value={formData.shippingProvince}
                                    error={errors.shippingProvince}
                                    disabled={sameAsBilling}
                                    onChange={e => handleChange("shippingProvince", e.currentTarget.value)}
                                    ref={el => (inputRefs.current.shippingProvince = el as HTMLInputElement)}
                                    className={errors.shippingProvince ? "error-border" : ""}
                                    required
                                >
                                    <option value="">{translate("Choose a Province")}</option>
                                    {states.map(state => (
                                        <option value={state.id} key={state.abbreviation} id={state.id}>
                                            {state.name}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]}>
                                <TextField
                                    label={
                                        <>
                                            {translate("Postal Code")}
                                            <Tooltip
                                                {...styles.toolTipStyles}
                                                text={siteMessage("postal_code_tooltip_message") as string}
                                            />
                                        </>
                                    }
                                    placeholder={translate("Postal Code")}
                                    data-test-selector="postal_code"
                                    value={formData.shippingPostalCode}
                                    {...styles.shippingInputStyle}
                                    disabled={sameAsBilling}
                                    error={errors.shippingPostalCode}
                                    onKeyDown={handlePostalCodeKeyDown}
                                    onInput={handlePostalCodeInput}  
                                    onChange={e => handleChange("shippingPostalCode", e.currentTarget.value)}
                                    ref={el => (inputRefs.current.shippingPostalCode = el as HTMLInputElement)}
                                    maxLength={6}
                                    required
                                />
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]}>
                                <TextField
                                    {...styles.countrySelction}
                                    label={translate("Country")}
                                    placeholder={canada ? canada.name : translate("Select Country")}
                                    value={canada ? canada.name : ""}
                                    data-test-selector="country"
                                    readOnly={true}
                                    disabled
                                    maxLength={20}
                                />
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </StyledWrapper>

                {/* Button  Section */}
                <StyledWrapper {...styles.btnWrapper}>
                    <Button variant="secondary" onClick={redirectToPreviousState} {...styles.previousBtnStyles}>
                        {translate("Previous")}
                    </Button>
                    <Button onClick={saveAndContinue} {...styles.proceedBtnStyles}>
                        {translate("Save & Continue")}
                    </Button>
                </StyledWrapper>
            </StyledForm>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(GuestUserAddressInfoForm)),
    definition: {
        displayName: "Guest User Billing & Shipping Information Form",
        group: "Common",
    },
};

export default widgetModule;

{
    /* EC-235 : SubTask - EC-528 = Wizard Structure of the form (Level 2) i.e Customer Billing and Shipping Info */
}
